import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { attendanceService, studentsService } from "@/lib/services";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Loader2, Save, Sun, X, Clock, FileText, Download, Upload, CalendarRange } from "lucide-react";
import { toast } from "sonner";

type Status = "present" | "absent" | "late" | "excused" | "holiday";
const STATUS_VARIANT: Record<Status, any> = {
  present: "success", absent: "destructive", late: "warning", excused: "info", holiday: "secondary",
};
const STATUS_ICON: Record<Status, any> = { present: Sun, absent: X, late: Clock, excused: FileText, holiday: CalendarRange };

export function AttendancePage() {
  const { user } = useAuth();
  if (!user) return null;

  if (user.role === "admin" || user.role === "principal" || user.role === "teacher" || user.role === "examhead") return <TeacherView />;
  return <StudentParentView />;
}

// ---------------- ADMIN / TEACHER VIEW ----------------
function TeacherView() {
  const { user } = useAuth();
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [klass, setKlass] = useState<string>(user?.class_name || "Grade 5A");
  const [drafts, setDrafts] = useState<Record<string, Status>>({});

  const { data: students = [] } = useQuery({
    queryKey: ["students"], queryFn: () => studentsService.list(),
  });
  const inClass = useMemo(() => students.filter((s) => s.class_name === klass), [students, klass]);
  const { data: existing = [], isPending } = useQuery({
    queryKey: ["attendance", klass, date],
    queryFn: () => attendanceService.list({ class_name: klass, date }),
    enabled: !!klass && !!date,
  });

  useEffect(() => {
    const map: Record<string, Status> = {};
    existing.forEach((r) => { map[r.student_id] = r.status as Status; });
    setDrafts(map);
  }, [existing]);

  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: () => attendanceService.bulkUpsert(
      Object.entries(drafts).map(([student_id, status]) => ({
        student_id, date, status, class_name: klass,
      })),
    ),
    onSuccess: (r) => {
      toast.success(`Saved ${r.upserted} record(s)`);
      qc.invalidateQueries({ queryKey: ["attendance"] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  const setAll = (s: Status) => {
    const map: Record<string, Status> = {};
    inClass.forEach((st) => { map[st.id] = s; });
    setDrafts(map);
  };

  const classes = Array.from(new Set(students.map((s) => s.class_name)));
  const filled = Object.keys(drafts).length;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Attendance</h1>
        <p className="mt-1 text-sm text-muted-foreground">Daily roll-call grid — set status per student, save in bulk.</p>
      </div>

      <Card><CardContent className="flex flex-wrap items-end gap-3 p-4">
        <div><Label>Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></div>
        <div>
          <Label>Class</Label>
          <div className="mt-1 flex flex-wrap gap-1">
            {classes.length === 0 && <Badge variant="outline">No classes yet</Badge>}
            {classes.map((c) => (
              <Button key={c} size="sm" variant={klass === c ? "default" : "outline"} onClick={() => setKlass(c)}>{c}</Button>
            ))}
          </div>
        </div>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setAll("present")}><Sun className="mr-2 h-4 w-4" /> All present</Button>
          <Button size="sm" variant="outline" onClick={() => setDrafts({})}>Clear</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending || filled === 0}>
            {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-2 h-4 w-4" /> Save ({filled})</>}
          </Button>
        </div>
      </CardContent></Card>

      {(user?.role === "admin" || user?.role === "principal" || user?.role === "examhead") && (
        <HistoricalImportPanel className={klass} hasStudents={inClass.length > 0} />
      )}

      <Card><CardContent className="p-0">
        {isPending ? <Loader2 className="m-12 h-6 w-6 animate-spin" /> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Roll</TableHead><TableHead>Student</TableHead><TableHead className="w-[420px]">Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {inClass.map((s) => {
                const cur = drafts[s.id];
                return (
                  <TableRow key={s.id}>
                    <TableCell><Badge variant="secondary">{s.roll_no}</Badge></TableCell>
                    <TableCell className="font-semibold">{s.name}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {(["present", "absent", "late", "excused"] as Status[]).map((st) => {
                          const Icon = STATUS_ICON[st];
                          return (
                            <Button key={st} size="sm" variant={cur === st ? "default" : "outline"}
                              onClick={() => setDrafts({ ...drafts, [s.id]: st })} className="capitalize">
                              <Icon className="mr-1 h-3 w-3" /> {st}
                            </Button>
                          );
                        })}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {inClass.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground">No students in this class.</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>
    </div>
  );
}

// ---------------- HISTORICAL IMPORT (admin + examhead) ----------------
function HistoricalImportPanel({ className, hasStudents }: { className: string; hasStudents: boolean }) {
  const qc = useQueryClient();
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [importing, setImporting] = useState(false);
  const [summary, setSummary] = useState<{ upserted: number; students_matched: number; total_rows: number } | null>(null);
  const [skip, setSkip] = useState<{ row: number; gr_no: string; name: string; reason: string }[] | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleExport = async () => {
    if (!className) { toast.error("Select a class first"); return; }
    if (!month) { toast.error("Pick a month"); return; }
    try {
      const blob = await attendanceService.exportMonthlyTemplate(className, month);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Attendance_${className}_${month}.xlsx`.replace(/\s+/g, "_");
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Attendance template downloaded");
    } catch (e: any) {
      toast.error(e.message || "Export failed");
    }
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!className) { toast.error("Select a class first"); return; }
    if (!month) { toast.error("Pick a month"); return; }
    setImporting(true); setSkip(null); setSummary(null);
    try {
      const r = await attendanceService.importMonthly(className, month, f);
      setSummary({ upserted: r.upserted, students_matched: r.students_matched, total_rows: r.total_rows });
      setSkip(r.skipped);
      qc.invalidateQueries({ queryKey: ["attendance"] });
      if (r.skipped.length === 0) toast.success(`Imported ${r.upserted} day-record(s) for ${r.students_matched} student(s)`);
      else toast.warning(`Imported ${r.upserted} record(s) · ${r.skipped.length} row issue(s)`, { duration: 8000 });
    } catch (e: any) {
      toast.error(e.message || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <Card data-testid="attendance-historical-panel">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <CalendarRange className="h-5 w-5 text-primary" /> Bulk Historical Import
        </CardTitle>
        <CardDescription>
          Admin / Exam Head only. Export a monthly template for <b>{className || "a class"}</b>, fill statuses (P/A/L/E/H), then import. Matched by <b>GR No.</b>; existing days are overwritten.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden"
          data-testid="attendance-import-file-input" onChange={handleImport} />
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs">Month</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
              className="mt-1 h-9 w-40" data-testid="attendance-month-input" />
          </div>
          <Button size="sm" variant="outline" onClick={handleExport} disabled={!hasStudents} data-testid="attendance-export-btn">
            <Download className="mr-2 h-4 w-4" /> Export Attendance Template
          </Button>
          <Button size="sm" variant="outline" disabled={importing || !hasStudents}
            onClick={() => fileRef.current?.click()} data-testid="attendance-import-btn">
            {importing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
            Import Attendance
          </Button>
          <span className="text-xs text-muted-foreground">
            {hasStudents ? "Codes: P=Present, A=Absent, L=Late, E=Excused, H=Holiday · blank = skip" : "Select a class with students above to enable export/import."}
          </span>
        </div>
        {summary && (
          <div
            className={`rounded-md border p-3 text-sm ${
              (skip?.length ?? 0) > 0
                ? "border-yellow-500/40 bg-yellow-50 text-yellow-900"
                : "border-green-500/40 bg-green-50 text-green-900"
            }`}
            data-testid="attendance-import-summary"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold">
                Import complete — {summary.upserted} day-record(s) across {summary.students_matched} student(s) of {summary.total_rows} row(s)
                {(skip?.length ?? 0) > 0 ? ` · ${skip!.length} row issue(s)` : ""}
              </span>
              <Button size="sm" variant="ghost" onClick={() => { setSummary(null); setSkip(null); }} data-testid="attendance-import-summary-dismiss">
                <X className="h-4 w-4" />
              </Button>
            </div>
            {(skip?.length ?? 0) > 0 && (
              <div className="mt-2 max-h-40 overflow-y-auto rounded border border-yellow-500/30 bg-white/60" data-testid="attendance-skip-list">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-yellow-100 text-yellow-900">
                    <tr className="text-left">
                      <th className="p-2 w-14">Row</th>
                      <th className="p-2 w-24">GR No.</th>
                      <th className="p-2">Name</th>
                      <th className="p-2">Issue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {skip!.map((s, i) => (
                      <tr key={i} className="border-t border-yellow-500/20" data-testid={`attendance-skip-row-${i}`}>
                        <td className="p-2 tabular-nums">{s.row}</td>
                        <td className="p-2 tabular-nums">{s.gr_no || "—"}</td>
                        <td className="p-2">{s.name || "—"}</td>
                        <td className="p-2">{s.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------- STUDENT / PARENT VIEW ----------------
function StudentParentView() {
  const { data: rows = [], isPending } = useQuery({
    queryKey: ["attendance", "mine"],
    queryFn: () => attendanceService.myList(),
  });

  const counts = useMemo(() => {
    const c = { present: 0, absent: 0, late: 0, excused: 0 };
    rows.forEach((r) => { (c as any)[r.status]++; });
    return c;
  }, [rows]);
  const total = rows.length;
  const pct = total ? Math.round(((counts.present + counts.late) / total) * 100) : 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Attendance</h1>
        <p className="mt-1 text-sm text-muted-foreground">Your attendance history and statistics.</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardHeader className="pb-2"><CardDescription className="text-xs uppercase tracking-wider">Attendance %</CardDescription></CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold tabular-nums ${pct >= 80 ? "text-emerald-700" : pct >= 60 ? "text-amber-700" : "text-red-700"}`}>{pct}%</div>
          </CardContent>
        </Card>
        <StatTile label="Present" value={counts.present} tint="text-emerald-700" />
        <StatTile label="Absent" value={counts.absent} tint="text-red-700" />
        <StatTile label="Late" value={counts.late} tint="text-amber-700" />
        <StatTile label="Excused" value={counts.excused} tint="text-sky-700" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>History</CardTitle>
          <CardDescription>{total} record(s) on file</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {isPending ? <Loader2 className="m-12 h-6 w-6 animate-spin" /> : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Date</TableHead><TableHead>Class</TableHead><TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground">No attendance recorded yet.</TableCell></TableRow>
                ) : (
                  rows
                    .sort((a, b) => b.date.localeCompare(a.date))
                    .map((r) => {
                      const Icon = STATUS_ICON[r.status as Status];
                      return (
                        <TableRow key={r.id}>
                          <TableCell className="tabular-nums">{r.date}</TableCell>
                          <TableCell className="text-muted-foreground">{r.class_name}</TableCell>
                          <TableCell>
                            <Badge variant={STATUS_VARIANT[r.status as Status]}>
                              <Icon className="mr-1 h-3 w-3" /> <span className="capitalize">{r.status}</span>
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function StatTile({ label, value, tint }: { label: string; value: number; tint: string }) {
  return (
    <Card>
      <CardHeader className="pb-2"><CardDescription className="text-xs uppercase tracking-wider">{label}</CardDescription></CardHeader>
      <CardContent>
        <div className={`text-3xl font-bold tabular-nums ${tint}`}>{value}</div>
      </CardContent>
    </Card>
  );
}
